import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { UI, LANG } from "../data/content";
import { Reveal, HeroBand, ArticleCard, SubscribeForm, usePageMeta } from "../components/kit";

const GEO_KEYS = ["cy", "gr", "eu"];

const GrantCalendar = () => {
  const [calls, setCalls] = useState([]);
  const [geo, setGeo] = useState(null);
  useEffect(() => { api.get("/grant-calls").then(({ data }) => setCalls(data)).catch(() => {}); }, []);
  const C = UI.calendar;
  const locale = LANG === "el" ? "el-GR" : "en-GB";
  const filtered = geo ? calls.filter((c) => c.geography === geo) : calls;
  const groups = [];
  filtered.forEach((c) => {
    const key = c.deadline.slice(0, 7);
    const g = groups.find((x) => x.key === key);
    if (g) g.items.push(c); else groups.push({ key, items: [c] });
  });
  return (
    <section className="k-section !py-14 bg-paper-alt" data-testid="grant-calendar-section">
      <div className="k-container">
        <p className="eyebrow mb-3">{C.eyebrow}</p>
        <h2 className="k-h2 mb-4">{C.title}</h2>
        <p className="text-sm text-ink-muted max-w-3xl mb-8">{C.lead}</p>
        <div className="flex flex-wrap gap-2 mb-10" data-testid="calendar-geo-filters">
          <button onClick={() => setGeo(null)}
            className={`topic-tag !py-2 !px-4 cursor-pointer border-0 transition-colors ${!geo ? "!bg-[var(--accent)] !text-white" : ""}`}
            data-testid="calendar-filter-all">{C.all}</button>
          {GEO_KEYS.map((g) => (
            <button key={g} onClick={() => setGeo(g)}
              className={`topic-tag !py-2 !px-4 cursor-pointer border-0 transition-colors ${geo === g ? "!bg-[var(--accent)] !text-white" : ""}`}
              data-testid={`calendar-filter-${g}`}>{C[g]}</button>
          ))}
        </div>
        {groups.length === 0 ? (
          <p className="text-ink-muted" data-testid="calendar-empty">{C.none}</p>
        ) : groups.map(({ key, items }) => (
          <div key={key} className="mb-10" data-testid={`calendar-month-${key}`}>
            <h3 className="text-base font-semibold capitalize border-b-2 border-[var(--accent)] inline-block pb-2">
              {new Date(`${key}-01T00:00:00`).toLocaleDateString(locale, { month: "long", year: "numeric" })}
            </h3>
            <div className="border-t border-line">
              {items.map((c) => (
                <div key={c.call_id} className="flex flex-wrap items-baseline gap-x-6 gap-y-2 py-5 border-b border-line" data-testid={`grant-call-${c.call_id}`}>
                  <span className="text-brand font-semibold whitespace-nowrap w-24" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {new Date(`${c.deadline}T00:00:00`).toLocaleDateString(locale, { day: "numeric", month: "short" })}
                  </span>
                  <div className="flex-1 min-w-[240px]">
                    <p className="font-medium">{c.title}</p>
                    <p className="text-xs text-ink-muted mt-1">{c.programme}{c.summary ? ` — ${c.summary}` : ""}</p>
                  </div>
                  <span className="topic-tag">{C[c.geography]}</span>
                  {c.url && (
                    <a href={c.url} target="_blank" rel="noreferrer" className="text-brand text-sm underline whitespace-nowrap" data-testid={`grant-call-link-${c.call_id}`}>{C.details}</a>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Insights() {
  const { topic } = useParams();
  const [topics, setTopics] = useState([]);
  const [data, setData] = useState({ items: [], pages: 1 });
  const [page, setPage] = useState(1);
  const [activeTopic, setActiveTopic] = useState(topic || null);

  useEffect(() => { setActiveTopic(topic || null); setPage(1); }, [topic]);
  useEffect(() => { api.get("/topics").then(({ data }) => setTopics(data)).catch(() => {}); }, []);
  useEffect(() => {
    const q = new URLSearchParams({ page, limit: 12 });
    if (activeTopic) q.set("topic", activeTopic);
    api.get(`/articles?${q}`).then(({ data }) => setData(data)).catch(() => {});
  }, [activeTopic, page]);

  const T = UI.pages.insights;
  const base = LANG === "el" ? "/el" : "";
  const topicName = topics.find((t) => t.slug === activeTopic)?.name;
  usePageMeta(
    topicName ? `${topicName} | Insights | KairosHK` : `${T.title} | KairosHK`,
    T.metaDesc
  );

  const featured = !activeTopic && page === 1 ? data.items.find((a) => a.featured) : null;
  const rest = featured ? data.items.filter((a) => a.slug !== featured.slug) : data.items;

  return (
    <div data-testid="insights-page">
      <HeroBand image="skyline" eyebrow={T.eyebrow} title={topicName || T.title}
        standfirst={T.standfirst}
        compact testId="insights-hero" />

      <GrantCalendar />

      <section className="k-section">
        <div className="k-container">
          <Reveal className="flex flex-wrap gap-2 mb-12" data-testid="topic-filters">
            <button
              onClick={() => { setActiveTopic(null); setPage(1); window.history.replaceState(null, "", `${base}/insights`); }}
              className={`topic-tag !py-2 !px-4 cursor-pointer border-0 transition-colors ${!activeTopic ? "!bg-[var(--accent)] !text-white" : ""}`}
              data-testid="topic-filter-all">
              {T.allTopics}
            </button>
            {topics.map((t) => (
              <button key={t.slug}
                onClick={() => { setActiveTopic(t.slug); setPage(1); window.history.replaceState(null, "", `${base}/insights/topics/${t.slug}`); }}
                className={`topic-tag !py-2 !px-4 cursor-pointer border-0 transition-colors ${activeTopic === t.slug ? "!bg-[var(--accent)] !text-white" : ""}`}
                data-testid={`topic-filter-${t.slug}`}>
                {t.name} {t.count > 0 && <span style={{ fontVariantNumeric: "tabular-nums" }}>({t.count})</span>}
              </button>
            ))}
          </Reveal>

          {featured && (
            <Reveal className="mb-4">
              <div className="border border-line p-8 md:p-12 bg-paper-alt hover:border-[var(--accent)] transition-colors">
                <p className="eyebrow mb-6">{T.featured}</p>
                <ArticleCard a={featured} featured />
              </div>
            </Reveal>
          )}

          <Reveal>
            {rest.length === 0 && !featured ? (
              <p className="text-ink-muted py-12" data-testid="no-articles-message">{T.none}</p>
            ) : (
              <div className="border-b border-line">
                {rest.map((a) => <ArticleCard key={a.slug} a={a} />)}
              </div>
            )}
          </Reveal>

          {data.pages > 1 && (
            <div className="flex gap-3 mt-10" data-testid="pagination">
              {Array.from({ length: data.pages }, (_, i) => (
                <button key={i} onClick={() => setPage(i + 1)}
                  className={`px-4 py-2 text-sm border cursor-pointer ${page === i + 1 ? "border-[var(--accent)] text-brand" : "border-line text-ink-muted"} bg-transparent`}
                  data-testid={`pagination-page-${i + 1}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}

          <div className="mt-24 border-t border-line pt-14" id="subscribe">
            <p className="eyebrow mb-4">{T.subscribe}</p>
            <SubscribeForm />
            <p className="text-xs text-ink-muted mt-6">
              {T.rssPre}<a href={`${process.env.REACT_APP_BACKEND_URL}/api/insights/rss.xml`} className="text-brand underline" data-testid="rss-link">{T.rssLink}</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
